import React from 'react';

// Component to render Notion blocks
export default function NotionRenderer({ blocks }) {
  if (!blocks || blocks.length === 0) {
    return <p className="text-gray-500">No content available.</p>;
  }

  return (
    <div className="notion-content">
      {blocks.map((block) => (
        <NotionBlock key={block.id} block={block} />
      ))}
    </div>
  );
}

function NotionBlock({ block }) {
  const { type } = block;
  const value = block[type];

  const renderRichText = (richTextArray) => {
    if (!richTextArray) return null;

    return richTextArray.map((text, index) => {
      let content = text.plain_text;

      if (text.href) {
        content = (
          <a
            key={index}
            href={text.href}
            className="text-blue-600 hover:text-blue-800 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {content}
          </a>
        );
      }

      if (text.annotations?.bold) {
        content = <strong key={index}>{content}</strong>;
      }
      if (text.annotations?.italic) {
        content = <em key={index}>{content}</em>;
      }
      if (text.annotations?.code) {
        content = (
          <code key={index} className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-red-600">
            {content}
          </code>
        );
      }
      if (text.annotations?.strikethrough) {
        content = <s key={index}>{content}</s>;
      }
      if (text.annotations?.underline) {
        content = <u key={index}>{content}</u>;
      }

      return <span key={index}>{content}</span>;
    });
  };

  switch (type) {
    case 'paragraph':
      return (
        <p className="mb-4 text-gray-800 leading-relaxed">
          {renderRichText(value.rich_text)}
        </p>
      );

    case 'heading_1':
      return (
        <h1 className="text-3xl font-bold mt-8 mb-4 text-gray-900">
          {renderRichText(value.rich_text)}
        </h1>
      );

    case 'heading_2':
      return (
        <h2 className="text-2xl font-bold mt-6 mb-3 text-gray-900">
          {renderRichText(value.rich_text)}
        </h2>
      );

    case 'heading_3':
      return (
        <h3 className="text-xl font-semibold mt-5 mb-2 text-gray-900">
          {renderRichText(value.rich_text)}
        </h3>
      );

    case 'bulleted_list_item':
      return (
        <li className="ml-6 mb-2 text-gray-800 list-disc">
          {renderRichText(value.rich_text)}
          {block.children && (
            <ul className="mt-2">
              {block.children.map(child => (
                <NotionBlock key={child.id} block={child} />
              ))}
            </ul>
          )}
        </li>
      );

    case 'numbered_list_item':
      return (
        <li className="ml-6 mb-2 text-gray-800 list-decimal">
          {renderRichText(value.rich_text)}
          {block.children && (
            <ol className="mt-2">
              {block.children.map(child => (
                <NotionBlock key={child.id} block={child} />
              ))}
            </ol>
          )}
        </li>
      );

    case 'code':
      return (
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
          <code className="text-sm font-mono">
            {value.rich_text.map(text => text.plain_text).join('')}
          </code>
        </pre>
      );

    case 'quote':
      return (
        <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-4 italic text-gray-700 bg-blue-50">
          {renderRichText(value.rich_text)}
        </blockquote>
      );

    case 'callout':
      return (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded">
          <div className="flex items-start">
            {value.icon && (
              <span className="mr-3 text-xl">
                {value.icon.emoji || '💡'}
              </span>
            )}
            <div className="flex-1">
              {renderRichText(value.rich_text)}
            </div>
          </div>
        </div>
      );

    case 'divider':
      return <hr className="my-8 border-gray-300" />;

    case 'image':
      const imageUrl = value.file?.url || value.external?.url;
      const caption = value.caption?.map(text => text.plain_text).join('') || '';
      return (
        <figure className="my-6">
          <img
            src={imageUrl}
            alt={caption}
            className="w-full rounded-lg shadow-md"
          />
          {caption && (
            <figcaption className="text-center text-sm text-gray-600 mt-2 italic">
              {caption}
            </figcaption>
          )}
        </figure>
      );

    case 'video':
      const videoUrl = value.file?.url || value.external?.url;
      return (
        <div className="my-6">
          <video
            src={videoUrl}
            controls
            className="w-full rounded-lg shadow-md"
          />
        </div>
      );

    case 'embed':
      return (
        <div className="my-6">
          <iframe
            src={value.url}
            title="Embedded content"
            className="w-full aspect-video rounded-lg shadow-md"
            allowFullScreen
          />
        </div>
      );

    case 'bookmark':
      return (
        <a
          href={value.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block my-4 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div className="text-blue-600 font-medium">{value.url}</div>
          {value.caption && (
            <div className="text-sm text-gray-600 mt-1">
              {renderRichText(value.caption)}
            </div>
          )}
        </a>
      );

    case 'table':
      return (
        <div className="overflow-x-auto my-6">
          <table className="min-w-full border border-gray-300">
            <tbody>
              {block.children?.map((row, index) => (
                <tr key={row.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  {row.table_row?.cells?.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-gray-300 px-4 py-2">
                      {renderRichText(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'toggle':
      return (
        <details className="my-4 border border-gray-300 rounded p-4">
          <summary className="cursor-pointer font-medium text-gray-900">
            {renderRichText(value.rich_text)}
          </summary>
          <div className="mt-2 pl-4">
            {block.children?.map(child => (
              <NotionBlock key={child.id} block={child} />
            ))}
          </div>
        </details>
      );

    default:
      return (
        <div className="my-2 text-gray-600">
          <small>Unsupported block type: {type}</small>
        </div>
      );
  }
}
